import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  Eye,
  GitCompare,
  Terminal,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { parseFormattedText } from '@/lib/textFormatting';
import { createDiff, renderCommandWithPlaceholders } from './helpers';

// Read-only YAML fix card. Mirrors IncidentDetail's YAMLFixCard visual style but
// removes all persisted-incident actions (apply, deploy-to-GitHub, safe-apply gating).
export interface ReadOnlyYamlFix {
  file_path: string;
  file_type: string;
  yaml_content: string;
  existing_yaml?: string;
  explanation: string;
  resource_name?: string;
  resource_kind?: string;
  resource_namespace?: string;
}

export function ReadOnlyYAMLFixCard({ fix, index }: { fix: ReadOnlyYamlFix; index: number }) {
  const [isOpen, setIsOpen] = useState(index === 0);
  const [copied, setCopied] = useState(false);
  const [showDiff, setShowDiff] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(fix.yaml_content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([fix.yaml_content], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (fix.file_path || '').split('/').pop() || 'fix.yaml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg">
        <CollapsibleTrigger className="w-full hover:bg-muted/50 transition-colors rounded-t-lg">
          <div className="flex items-center justify-between p-4 min-w-0">
            <div className="flex items-center gap-3 min-w-0">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
              <div className="flex items-center gap-2 min-w-0">
                <Badge variant="outline" className="font-mono text-xs flex-shrink-0">
                  {fix.file_type}
                </Badge>
                <span className="font-medium truncate">{fix.file_path}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {fix.resource_kind && (
                <Badge variant="secondary" className="text-xs">
                  {fix.resource_kind}
                </Badge>
              )}
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-4">
            <Separator />

            <div>
              <h4 className="text-sm font-semibold mb-2">Explanation</h4>
              <p className="text-sm text-muted-foreground">{parseFormattedText(fix.explanation)}</p>
            </div>

            {(fix.resource_name || fix.resource_namespace) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                {fix.resource_namespace && (
                  <div>
                    <span className="text-muted-foreground">Namespace:</span>
                    <span className="ml-2 font-mono">{fix.resource_namespace}</span>
                  </div>
                )}
                {fix.resource_name && (
                  <div>
                    <span className="text-muted-foreground">Resource:</span>
                    <span className="ml-2 font-mono">{fix.resource_name}</span>
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h4 className="text-sm font-semibold">YAML Configuration</h4>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy();
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload();
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>

              {fix.existing_yaml && fix.file_type === 'update' && (
                <div className="flex justify-center mb-3">
                  <div className="flex items-center gap-2 bg-muted p-1 rounded-md">
                    <Button
                      variant={!showDiff ? 'default' : 'ghost'}
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDiff(false);
                      }}
                      className="text-xs h-6"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Updated YAML
                    </Button>
                    <Button
                      variant={showDiff ? 'default' : 'ghost'}
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDiff(true);
                      }}
                      className="text-xs h-6"
                    >
                      <GitCompare className="h-3 w-3 mr-1" />
                      Show Diff
                    </Button>
                  </div>
                </div>
              )}

              {fix.existing_yaml && fix.file_type === 'update' && showDiff ? (
                <div className="bg-muted p-3 rounded-md border font-mono text-xs overflow-x-auto max-h-96">
                  {createDiff(fix.existing_yaml, fix.yaml_content).map((diffLine, lineIndex) => (
                    <div
                      key={lineIndex}
                      className={cn(
                        'whitespace-pre-wrap flex',
                        diffLine.type === 'removed' &&
                          'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200',
                        diffLine.type === 'added' &&
                          'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200',
                        diffLine.type === 'unchanged' && 'text-foreground hover:bg-muted/50',
                      )}
                    >
                      <span
                        className={cn(
                          'inline-block w-6 text-right mr-2 text-muted-foreground select-none flex-shrink-0 border-r border-border/50 pr-1',
                          diffLine.type === 'removed' &&
                            'text-red-600 dark:text-red-400 bg-red-100/50 dark:bg-red-900/30',
                          diffLine.type === 'added' &&
                            'text-green-600 dark:text-green-400 bg-green-100/50 dark:bg-green-900/30',
                        )}
                      >
                        {diffLine.type === 'removed' ? '-' : diffLine.type === 'added' ? '+' : ' '}
                      </span>
                      <span className="flex-1 min-w-0">{diffLine.line}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <pre
                  className={cn(
                    'p-4 rounded-md border font-mono text-xs overflow-x-auto max-h-96',
                    fix.file_type === 'delete'
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      : fix.file_type === 'create'
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                        : 'bg-muted',
                  )}
                >
                  <code className="text-foreground">
                    {fix.file_type === 'delete' && fix.existing_yaml
                      ? fix.existing_yaml
                      : fix.yaml_content}
                  </code>
                </pre>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// Read-only code fix card. Mirrors IncidentDetail's CodeFixCard but removes the
// "View Pull Request" action (which depends on a persisted incident).
export interface ReadOnlyCodeFix {
  file_path: string;
  file_type: string;
  original_code?: string;
  fixed_code: string;
  explanation: string;
  line_start: number;
  line_end: number;
  language: string;
}

function getLanguageColor(lang: string) {
  switch ((lang || '').toLowerCase()) {
    case 'python':
      return 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400';
    case 'go':
      return 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-400';
    case 'javascript':
      return 'bg-amber-500/20 text-amber-700 dark:text-amber-400';
    case 'typescript':
      return 'bg-blue-500/20 text-blue-700 dark:text-blue-400';
    case 'java':
      return 'bg-red-500/20 text-red-700 dark:text-red-400';
    case 'rust':
      return 'bg-orange-500/20 text-orange-700 dark:text-orange-400';
    default:
      return 'bg-gray-500/20 text-gray-700 dark:text-gray-400';
  }
}

export function ReadOnlyCodeFixCard({ fix, index }: { fix: ReadOnlyCodeFix; index: number }) {
  const [isOpen, setIsOpen] = useState(index === 0);
  const [copied, setCopied] = useState(false);
  const [showDiff, setShowDiff] = useState(!!fix.original_code);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fix.fixed_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (non-HTTPS, iframe, etc.)
    }
  };

  const handleDownload = () => {
    const filePath = fix.file_path || '';
    const blob = new Blob([fix.fixed_code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filePath.split('/').pop() || 'code.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Only surface the line range when the agent actually provided one; otherwise
  // we'd render a meaningless "Lines 0-0" badge.
  const hasLineRange = fix.line_start > 0 && fix.line_end > 0;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg overflow-hidden">
        <CollapsibleTrigger className="w-full hover:bg-muted/50 transition-colors rounded-t-lg">
          <div className="flex items-center justify-between p-4 gap-2">
            <div className="flex items-center gap-3 min-w-0">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
              <div className="flex items-center gap-2 min-w-0">
                <Badge className={cn('font-mono text-xs flex-shrink-0', getLanguageColor(fix.language))}>
                  {fix.language}
                </Badge>
                <span className="font-medium truncate">{fix.file_path}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {hasLineRange && (
                <Badge variant="outline" className="text-xs whitespace-nowrap">
                  Lines {fix.line_start}-{fix.line_end}
                </Badge>
              )}
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="p-4 pt-0 space-y-4 overflow-hidden">
            <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md border">
              <strong className="text-foreground">Fix Explanation:</strong>
              <div className="mt-1">{parseFormattedText(fix.explanation)}</div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {fix.original_code && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDiff(!showDiff);
                  }}
                  className="flex items-center gap-1"
                >
                  <Eye className="h-3 w-3" />
                  {showDiff ? 'Show Fixed Code' : 'Show Diff'}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy();
                }}
                className="flex items-center gap-1"
              >
                <Copy className="h-3 w-3" />
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
                className="flex items-center gap-1"
              >
                <Download className="h-3 w-3" />
                Download
              </Button>
            </div>

            <div className="overflow-hidden">
              {showDiff && fix.original_code ? (
                <div className="rounded-md border overflow-hidden max-w-full">
                  <div className="bg-muted px-4 py-2 text-xs font-medium flex items-center justify-between border-b">
                    <span>Changes</span>
                    {hasLineRange && (
                      <span className="text-muted-foreground">
                        Lines {fix.line_start}-{fix.line_end}
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-xs overflow-x-auto max-h-96">
                    {createDiff(fix.original_code, fix.fixed_code).map((line, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          'px-4 py-0.5 whitespace-pre',
                          line.type === 'removed' &&
                            'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
                          line.type === 'added' &&
                            'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
                          line.type === 'unchanged' && 'bg-background',
                        )}
                      >
                        <span className="select-none mr-4 text-muted-foreground w-8 inline-block text-right">
                          {line.lineNumber}
                        </span>
                        <span className="select-none mr-2">
                          {line.type === 'removed' ? '-' : line.type === 'added' ? '+' : ' '}
                        </span>
                        {line.line}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <pre className="p-4 rounded-md border font-mono text-xs overflow-x-auto max-h-96 bg-muted">
                  <code className="text-foreground">{fix.fixed_code}</code>
                </pre>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// Read-only cloud CLI fix card (AWS api_fixes / OCI oci_cli_fixes).
// Removes Apply/Rollback/Validate/Edit-placeholder actions.
export interface ReadOnlyCloudCliFix {
  service?: string;
  operation?: string;
  explanation?: string;
  cli_command?: string;
  rollback_command?: string;
  risk_level?: string;
  resource_arn?: string;
  resource_ocid?: string;
}

export function ReadOnlyCloudCliFixCard({
  fix,
  index,
  riskBadgeVariant,
}: {
  fix: ReadOnlyCloudCliFix;
  index: number;
  riskBadgeVariant: 'default' | 'destructive' | 'outline' | 'secondary';
}) {
  const [isOpen, setIsOpen] = useState(index === 0);
  const [copied, setCopied] = useState(false);
  const resourceIdentifier = fix.resource_arn || fix.resource_ocid || '';

  const handleCopy = () => {
    if (!fix.cli_command) return;
    navigator.clipboard.writeText(fix.cli_command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg">
        <CollapsibleTrigger className="w-full hover:bg-muted/50 transition-colors rounded-t-lg">
          <div className="flex items-center justify-between p-4 gap-2 min-w-0">
            <div className="flex items-center gap-3 min-w-0">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
              <Terminal className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="font-medium truncate">
                {fix.service}
                {fix.operation ? `:${fix.operation}` : ''}
              </span>
            </div>
            {fix.risk_level && (
              <Badge variant={riskBadgeVariant} className="text-xs flex-shrink-0">
                {fix.risk_level} risk
              </Badge>
            )}
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-4">
            <Separator />

            {fix.explanation && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Explanation</h4>
                <p className="text-sm text-muted-foreground">{parseFormattedText(fix.explanation)}</p>
              </div>
            )}

            {resourceIdentifier && (
              <div className="text-sm">
                <span className="text-muted-foreground">Resource:</span>
                <span className="ml-2 font-mono break-all">{resourceIdentifier}</span>
              </div>
            )}

            {fix.cli_command && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold">CLI Command</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy();
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
                <pre className="p-4 rounded-md border font-mono text-xs overflow-x-auto max-h-96 bg-muted whitespace-pre-wrap">
                  {renderCommandWithPlaceholders(fix.cli_command)}
                </pre>
              </div>
            )}

            {fix.rollback_command && (
              <div>
                <h4 className="text-sm font-semibold mb-2">Rollback Command</h4>
                <pre className="p-4 rounded-md border font-mono text-xs overflow-x-auto max-h-96 bg-muted whitespace-pre-wrap">
                  {renderCommandWithPlaceholders(fix.rollback_command)}
                </pre>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
